import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Calendar, Download, Loader2, Pill, Stethoscope } from "lucide-react";
import { usePatientAuth } from "../../store/authPatientStore";
import PatientPageHeading from "../../components/organisms/patient-dashboard/PatientPageHeading";
import { getPatientPrescriptions, getPrescriptionPdf, type PatientPrescription } from "../../api/authPatient";
import { cn, downloadFileFromUrl } from "../../lib/utils";

export default function PatientPrescriptions() {
  const { patient, activeProfil } = usePatientAuth();
  const [prescriptions, setPrescriptions] = useState<PatientPrescription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pdfLoading, setPdfLoading] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const fullName = useMemo(
    () => (activeProfil ? `${activeProfil.nom} ${activeProfil.prenom}`.trim() : `${patient?.nom ?? ""} ${patient?.prenom ?? ""}`.trim()),
    [activeProfil, patient]
  );
  const patientNip = activeProfil?.nip?.trim() || patient?.nip?.trim() || "";

  useEffect(() => {
    let mounted = true;

    async function load() {
      if (!patientNip) {
        if (!mounted) return;
        setPrescriptions([]);
        setError("NIP patient introuvable. Impossible de charger les ordonnances.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");
      const result = await getPatientPrescriptions(patientNip);
      if (!mounted) return;

      if (!result.ok) {
        setPrescriptions([]);
        setError(result.message || "Impossible de charger les ordonnances.");
        setLoading(false);
        return;
      }

      setPrescriptions(result.data);
      setLoading(false);
    }

    void load();
    return () => {
      mounted = false;
    };
  }, [patientNip]);

  const formatDate = (value?: string) => {
    const source = value?.trim();
    if (!source) return "Date non renseignee";
    const parsed = new Date(source);
    if (Number.isNaN(parsed.getTime())) return source;
    return new Intl.DateTimeFormat("fr-FR", { dateStyle: "long", timeStyle: "short" }).format(parsed);
  };

  const getPrescriptionText = (item: PatientPrescription) => item.text || item.instructions || "Aucune instruction disponible.";

  const handleDownloadPdf = async (item: PatientPrescription) => {
    if (!item.id || pdfLoading) return;
    setPdfLoading(item.id);
    setPdfError(null);
    const result = await getPrescriptionPdf(item.id);
    setPdfLoading(null);
    if (!result.ok) {
      setPdfError(result.message || "Impossible de générer le PDF.");
      return;
    }
    await downloadFileFromUrl(result.pdfUrl, `ordonnance-${item.id}.pdf`);
  };

  return (
    <div className="space-y-6">
      <PatientPageHeading
        title="Ordonnances"
        description={fullName ? `Ordonnances associées à ${fullName}` : "Historique de vos ordonnances"}
      />

      <div className="rounded-[12px] border bg-white p-4 md:p-6" style={{ borderColor: "#E5E7EB" }}>
        {loading && <p className="text-sm text-gray-500">Chargement des ordonnances…</p>}

        {!loading && error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-[10px] px-3 py-2">{error}</div>
        )}

        {!loading && !error && prescriptions.length === 0 && (
          <div className="text-sm text-gray-600 bg-gray-50 border rounded-[10px] px-3 py-2" style={{ borderColor: "#E5E7EB" }}>
            Aucune ordonnance disponible pour le moment.
          </div>
        )}

        {pdfError && (
          <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-[10px] px-3 py-2">{pdfError}</div>
        )}

        {!loading && !error && prescriptions.length > 0 && (
          <div className="space-y-4 max-h-[640px] overflow-y-auto pr-1">
            {prescriptions.map((item) => (
              <div
                key={item.id ?? `${item.date}-${item.text}`}
                className="rounded-[10px] border bg-[#FAFBFB] p-4 md:p-5"
                style={{ borderColor: "#E5E7EB" }}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-[#0B1F16]">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] bg-[#DAF1E9]">
                      <Pill size={18} className="text-[#00553E]" />
                    </span>
                    <div>
                      <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
                        <Calendar size={12} />
                        {formatDate(item.date || item.datePrescription)}
                      </div>
                    </div>
                  </div>
                  {item.statut && (
                    <span className="inline-flex items-center rounded-full bg-[#EEF1F1] px-2.5 py-1 text-xs font-semibold text-[#0B1F16]">
                      {item.statut}
                    </span>
                  )}
                </div>

                <p className="mt-3 text-sm leading-relaxed text-[#334155]">{getPrescriptionText(item)}</p>

                <div className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                  <div className="flex items-start gap-2">
                    <Stethoscope size={14} className="mt-0.5 shrink-0 text-gray-400" />
                    <div>
                      <div className="text-xs text-gray-500">Professionnel</div>
                      <div className="font-semibold text-[#0B1F16] break-all">
                        {item.professionnelNip || "Non renseigné"}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Building2 size={14} className="mt-0.5 shrink-0 text-gray-400" />
                    <div>
                      <div className="text-xs text-gray-500">Établissement</div>
                      <div className="font-semibold text-[#0B1F16] break-all">
                        {item.etablissementId || "Non renseigné"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2 border-t pt-3" style={{ borderColor: "#E5E7EB" }}>
                  {item.consultationId ? (
                    <Link
                      to={`/patient/consultations?consultationId=${encodeURIComponent(item.consultationId)}`}
                      className="inline-flex h-9 items-center gap-1.5 rounded-[6px] border px-3 text-sm font-semibold text-[#0B1F16] hover:bg-white"
                      style={{ borderColor: "#E5E7EB" }}
                    >
                      <Stethoscope size={14} />
                      Voir la consultation
                    </Link>
                  ) : (
                    <span className="text-sm text-gray-400">Consultation source indisponible</span>
                  )}
                  <button
                    type="button"
                    onClick={() => void handleDownloadPdf(item)}
                    disabled={!item.id || pdfLoading === item.id}
                    className={cn(
                      "inline-flex h-9 items-center gap-1.5 rounded-[6px] px-3 text-sm font-semibold text-white disabled:opacity-50",
                    )}
                    style={{ background: "#00553E" }}
                  >
                    {pdfLoading === item.id ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Download size={14} />
                    )}
                    {pdfLoading === item.id ? "Génération…" : "Télécharger (PDF)"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

